import {
  AdminDashboardKpis,
  InstructorCourseSummary,
  InstructorDashboardKpis,
  InstructorQuizAttemptSummary,
  InstructorSubmissionSummary,
  LearnerDashboardKpis,
  LearnerEnrollmentSummary,
  RoleDashboardKpis,
} from '@/models/dashboard-kpi.model'
import { UserRole } from '@/models/user.model'
import { secureApiService } from './secureApiService'

type Nullable<T> = T | null | undefined

type LearnerKpisApiResponse = {
  learnerId?: string
  totalCourses?: number | string
  enrollments?: Array<Record<string, any>>
  modulesTotal?: number | string
  modulesCompleted?: number | string
  percentComplete?: number | string
  assignments?: Partial<Record<'drafts' | 'submitted' | 'graded', number | string>>
  quizzes?: Partial<Record<'attempts' | 'avgScore', number | string>>
  certificatesEarned?: number | string
  lastActiveAt?: string
}

type InstructorKpisApiResponse = {
  instructorId?: string
  coursesCount?: number | string
  courses?: Array<Record<string, any>>
  activeStudents?: number | string
  pendingSubmissions?: number | string
  avgStudentScorePct?: number | string
  recentSubmissions?: Array<Record<string, any>>
  recentQuizAttempts?: Array<Record<string, any>>
  contentUploads?: number | string
  courseCompletionRate?: number | string
}

type AdminKpisApiResponse = {
  usersByRole?: Record<string, number | string>
  activeLearners?: number | string
  totalCourses?: number | string
  activeCourses?: number | string
  totalProgrammes?: number | string
  enrollmentsByProgramme?: Array<Record<string, any>>
  applicationsPending?: number | string
  payments?: {
    totalAmount?: number | string
    count?: number | string
    period?: {
      from?: string
      to?: string
    }
  }
  globalCourseCompletionRate?: number | string
  systemUsage?: {
    logins7?: number | string
    logins30?: number | string
  }
}

const toNumber = (value: Nullable<number | string>, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

const toNullableString = (value: Nullable<unknown>): string | null => {
  if (value === null || value === undefined) return null
  return String(value)
}

const toStringSafe = (value: Nullable<unknown>, fallback = ''): string => {
  if (value === null || value === undefined) return fallback
  return String(value)
}

const unwrapPayload = <T extends Record<string, any>>(raw: unknown): T => {
  if (raw && typeof raw === 'object') {
    const candidate = raw as Record<string, any>
    if ('data' in candidate && typeof candidate.data === 'object' && candidate.data !== null) {
      return candidate.data as T
    }
    if ('result' in candidate && typeof candidate.result === 'object' && candidate.result !== null) {
      return candidate.result as T
    }
  }
  return (raw ?? {}) as T
}

const mapLearnerEnrollments = (items: Array<Record<string, any>> = []): LearnerEnrollmentSummary[] =>
  items.map((item) => ({
    enrollmentId: toStringSafe(item.enrollmentId ?? item.enrollment_id ?? ''),
    courseId: toStringSafe(item.courseId ?? item.course_id ?? ''),
    courseTitle: toStringSafe(item.courseTitle ?? item.course_title ?? 'Untitled course'),
    courseCode: toNullableString(item.courseCode ?? item.course_code ?? null),
    programmeId: toNullableString(item.programmeId ?? item.programme_id ?? null),
    enrollmentStatus: toStringSafe(item.enrollmentStatus ?? item.enrollment_status ?? 'unknown'),
    enrolledAt: toNullableString(item.enrolledAt ?? item.enrolled_at ?? null),
  }))

const mapInstructorCourses = (items: Array<Record<string, any>> = []): InstructorCourseSummary[] =>
  items.map((item) => {
    const completion = toNumber(item.completionRate ?? item.completion_rate ?? null, Number.NaN)
    return {
      courseId: toStringSafe(item.courseId ?? item.course_id ?? ''),
      courseTitle: toStringSafe(item.courseTitle ?? item.course_title ?? 'Untitled course'),
      courseCode: toNullableString(item.courseCode ?? item.course_code ?? null),
      enrollmentCount: toNumber(item.enrollmentCount ?? item.enrollments ?? item.enrollment_count ?? null, 0),
      completionRate: Number.isNaN(completion) ? null : completion,
      updatedAt: toNullableString(item.updatedAt ?? item.updated_at ?? null),
    }
  })

const mapInstructorSubmissions = (items: Array<Record<string, any>> = []): InstructorSubmissionSummary[] =>
  items.map((item) => ({
    submissionId: toStringSafe(item.submissionId ?? item.submission_id ?? ''),
    learnerName: toNullableString(item.learnerName ?? item.learner_name ?? null),
    courseTitle: toNullableString(item.courseTitle ?? item.course_title ?? null),
    submittedAt: toNullableString(item.submittedAt ?? item.submitted_at ?? null),
    status: toNullableString(item.status ?? null),
  }))

const mapInstructorQuizAttempts = (items: Array<Record<string, any>> = []): InstructorQuizAttemptSummary[] =>
  items.map((item) => {
    const score = toNumber(item.score ?? item.quiz_score ?? null, Number.NaN)
    return {
      attemptId: toStringSafe(item.attemptId ?? item.attempt_id ?? ''),
      learnerName: toNullableString(item.learnerName ?? item.learner_name ?? null),
      quizTitle: toNullableString(item.quizTitle ?? item.quiz_title ?? null),
      score: Number.isNaN(score) ? null : score,
      attemptedAt: toNullableString(item.attemptedAt ?? item.attempted_at ?? null),
    }
  })

const mapAdminProgrammeEnrollments = (items: Array<Record<string, any>> = []) =>
  items.map((item) => ({
    programmeId: toStringSafe(item.programmeId ?? item.programme_id ?? ''),
    programmeTitle: toStringSafe(item.programmeTitle ?? item.programme_title ?? 'Unknown programme'),
    enrollments: toNumber(item.enrollments ?? item.total ?? null, 0),
  }))

class KpiService {
  async getLearnerKpis(): Promise<LearnerDashboardKpis> {
    try {
      const response = await secureApiService.get<LearnerKpisApiResponse | { data: LearnerKpisApiResponse }>('/kpis/learner')
      const payload = unwrapPayload<LearnerKpisApiResponse>(response.data)

      return {
        role: UserRole.LEARNER,
        learnerId: toStringSafe(payload.learnerId ?? ''),
        totalCourses: toNumber(payload.totalCourses ?? 0),
        enrollments: mapLearnerEnrollments(payload.enrollments),
        modulesTotal: toNumber(payload.modulesTotal ?? 0),
        modulesCompleted: toNumber(payload.modulesCompleted ?? 0),
        percentComplete: toNumber(payload.percentComplete ?? 0),
        assignments: {
          drafts: toNumber(payload.assignments?.drafts ?? 0),
          submitted: toNumber(payload.assignments?.submitted ?? 0),
          graded: toNumber(payload.assignments?.graded ?? 0),
        },
        quizzes: {
          attempts: toNumber(payload.quizzes?.attempts ?? 0),
          avgScore: toNumber(payload.quizzes?.avgScore ?? 0),
        },
        certificatesEarned: toNumber(payload.certificatesEarned ?? 0),
        lastActiveAt: toNullableString(payload.lastActiveAt ?? null),
      }
    } catch (error) {
      console.error('[KpiService] Failed to fetch learner KPIs', error)
      throw error
    }
  }

  async getInstructorKpis(): Promise<InstructorDashboardKpis> {
    try {
      const response = await secureApiService.get<InstructorKpisApiResponse | { data: InstructorKpisApiResponse }>('/kpis/instructor/me')
      const payload = unwrapPayload<InstructorKpisApiResponse>(response.data)

      return {
        role: UserRole.INSTRUCTOR,
        instructorId: toStringSafe(payload.instructorId ?? ''),
        coursesCount: toNumber(payload.coursesCount ?? 0),
        courses: mapInstructorCourses(payload.courses),
        activeStudents: toNumber(payload.activeStudents ?? 0),
        pendingSubmissions: toNumber(payload.pendingSubmissions ?? 0),
        avgStudentScorePct: toNumber(payload.avgStudentScorePct ?? 0),
        recentSubmissions: mapInstructorSubmissions(payload.recentSubmissions),
        recentQuizAttempts: mapInstructorQuizAttempts(payload.recentQuizAttempts),
        contentUploads: toNumber(payload.contentUploads ?? 0),
        courseCompletionRate: toNumber(payload.courseCompletionRate ?? 0),
      }
    } catch (error) {
      console.error('[KpiService] Failed to fetch instructor KPIs', error)
      throw error
    }
  }

  async getAdminKpis(): Promise<AdminDashboardKpis> {
    try {
      const response = await secureApiService.get<AdminKpisApiResponse | { data: AdminKpisApiResponse }>('/kpis/admin')
      const payload = unwrapPayload<AdminKpisApiResponse>(response.data)

      return {
        role: UserRole.ADMIN,
        usersByRole: Object.entries(payload.usersByRole ?? {}).reduce<Record<string, number>>(
          (acc, [key, value]) => ({
            ...acc,
            [key]: toNumber(value ?? 0),
          }),
          {},
        ),
        activeLearners: toNumber(payload.activeLearners ?? 0),
        totalCourses: toNumber(payload.totalCourses ?? 0),
        activeCourses: toNumber(payload.activeCourses ?? 0),
        totalProgrammes: toNumber(payload.totalProgrammes ?? 0),
        enrollmentsByProgramme: mapAdminProgrammeEnrollments(payload.enrollmentsByProgramme),
        applicationsPending: toNumber(payload.applicationsPending ?? 0),
        payments: {
          totalAmount: toNumber(payload.payments?.totalAmount ?? 0),
          count: toNumber(payload.payments?.count ?? 0),
          period: {
            from: toNullableString(payload.payments?.period?.from ?? null),
            to: toNullableString(payload.payments?.period?.to ?? null),
          },
        },
        globalCourseCompletionRate: toNumber(payload.globalCourseCompletionRate ?? 0),
        systemUsage: {
          logins7: toNumber(payload.systemUsage?.logins7 ?? 0),
          logins30: toNumber(payload.systemUsage?.logins30 ?? 0),
        },
      }
    } catch (error) {
      console.error('[KpiService] Failed to fetch admin KPIs', error)
      throw error
    }
  }

  async getRoleKpis(role: UserRole | null): Promise<RoleDashboardKpis> {
    switch (role) {
      case UserRole.ADMIN:
        return this.getAdminKpis()
      case UserRole.INSTRUCTOR:
        return this.getInstructorKpis()
      case UserRole.LEARNER:
        return this.getLearnerKpis()
      default:
        throw new Error('Unsupported user role. Unable to load dashboard KPIs.')
    }
  }
}

export const kpiService = new KpiService()
